// form
import { useI18n } from 'iomart-hooks';
import { useFormContext, useController } from 'react-hook-form';
// @mui
import { Autocomplete, IconButton, InputAdornment, TextField, TextFieldProps, Tooltip } from '@mui/material';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { UseHook } from 'src/@types/common';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

// ----------------------------------------------------------------------

type IProps = {
  name: string;
  label: string;
  optionsLabel: string;
  useHook: UseHook;
  fields?: string[];
  filters?: { [key: string]: any };
  onChoose?: (value: any) => void;
};

type Props = IProps & TextFieldProps;
type Option = { id: number; name: string };

export default function RHFAuto({
  name,
  label,
  useHook,
  optionsLabel,
  disabled,
  fields = [],
  filters = {},
  onChoose,
  ...other
}: Props) {
  const { t } = useI18n(false);
  const { getList: loader } = useHook(false);
  const { control } = useFormContext();
  const {
    field,
    fieldState: { error }
  } = useController({
    name,
    control,
    defaultValue: null
  });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<Option[]>(field.value ? [field.value] : []);
  const [inputValue, setInputValue] = useState<string>(field?.value?.[optionsLabel] || '');
  const [debouncedSearch, setDebouncedSearch] = useState<string>(field?.value?.[optionsLabel] || '');
  const loadingRef = useRef(false);
  const loaderRef = useRef(loader);
  const filtersRef = useRef(filters);
  const fieldsRef = useRef(fields);
  loaderRef.current = loader;
  filtersRef.current = filters;
  fieldsRef.current = fields;

  const setDebouncedSearchDebounced = useMemo(
    () =>
      debounce((value: string) => {
        setDebouncedSearch(value);
      }, 400),
    []
  );

  useEffect(() => () => setDebouncedSearchDebounced.cancel(), [setDebouncedSearchDebounced]);

  useEffect(() => {
    const val = field?.value?.[optionsLabel] ?? '';
    setInputValue(val);
    setDebouncedSearch(val);
    if (field.value) {
      setOptions((prev) => {
        if (prev.length === 1 && prev[0]?.id === field.value?.id) return prev;
        return [field.value];
      });
    } else {
      setOptions([]);
    }
  }, [field?.value?.id, field?.value?.[optionsLabel], optionsLabel]);

  const handleInputChange = useCallback(
    (_: unknown, value: string) => {
      setInputValue(value);
      if (value === '') {
        setDebouncedSearchDebounced.cancel();
        setDebouncedSearch('');
      } else {
        setDebouncedSearchDebounced(value);
      }
    },
    [setDebouncedSearchDebounced]
  );

  const loadInitial = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    loader({
      paging: { page: 0, size: 10 },
      filters: { ...filters },
      category: 'active'
    })
      .then(({ items }) => {
        setOptions(
          items.map(({ id, name, ...item }) => ({
            id,
            name,
            ...fields.reduce((value, currentValue) => ({ ...value, [currentValue]: item[currentValue] }), {})
          }))
        );
        setOpen(true);
      })
      .catch(() => setOpen(false))
      .finally(() => {
        setLoading(false);
        loadingRef.current = false;
      });
  }, [loader, filters, fields]);

  useEffect(() => {
    if (!debouncedSearch) return;

    if (field?.value?.[optionsLabel] === debouncedSearch) return;

    let active = true;
    setLoading(true);

    loaderRef
      .current({
        filters: { name: debouncedSearch, ...filtersRef.current },
        category: 'active'
      })
      .then((res: { items: any[] }) => {
        if (!active) return;
        const { items } = res;
        const nextFields = fieldsRef.current;
        setOptions(
          items.map(({ id, name, ...item }) => {
            if (nextFields.length > 0) {
              return {
                id,
                name,
                ...nextFields.reduce(
                  (value, currentValue) => ({ ...value, [currentValue]: item[currentValue] }),
                  {} as Record<string, unknown>
                )
              };
            }
            return { id, name };
          })
        );
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [debouncedSearch, field?.value?.[optionsLabel], optionsLabel]);

  const handleOpen = useCallback(() => {
    if (options.length === 0 && !inputValue && !loadingRef.current) {
      loadInitial();
    } else if (options.length <= 1 && !loadingRef.current) {
      loadInitial();
      setOpen(true);
    } else {
      setOpen(true);
    }
  }, [options.length, inputValue, loadInitial]);

  const handleButtonClick = useCallback(
    (e: React.PointerEvent | React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (open) {
        setOpen(false);
      } else if (options.length === 0 && !inputValue) {
        loadInitial();
      } else {
        setOpen(true);
      }
    },
    [open, options.length, inputValue, loadInitial]
  );

  const singleOptionMatch = options.length === 1 && options[0]?.name === inputValue;

  return (
    <Autocomplete
      {...field}
      open={open}
      disabled={disabled}
      loading={loading}
      blurOnSelect="touch"
      onOpen={handleOpen}
      onClose={() => setOpen(false)}
      filterOptions={x => x}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      popupIcon={null}
      onChange={(_, newValue) => {
        if (onChoose) onChoose(newValue);
        field.onChange(newValue);
      }}
      getOptionLabel={option => (option && option[optionsLabel]) || ''}
      options={options}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      renderInput={params => (
        <Tooltip
          title={inputValue}
          disableHoverListener={!disabled}
          disableTouchListener={!disabled}
          disableFocusListener
          placement="top-start"
          arrow
        >
          <TextField
            {...params}
            label={t(label)}
            error={!!error}
            helperText={error?.message}
            {...other}
            InputProps={{
              ...params.InputProps,
              ...(!inputValue &&
                !singleOptionMatch && {
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <IconButton
                        type="button"
                        onPointerDown={handleButtonClick}
                        aria-label={t('Open list')}
                        size="small"
                        sx={{ padding: '2px' }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }),
            }}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{
              ...other.sx,
              '& .MuiInputBase-input': {
                font: 'menu'
              },
            }}
          />
        </Tooltip>
      )}
    />
  );
}
